import { memo, useMemo, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { format, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import { Download, Pin, PinOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip as UiTooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChartTooltip } from "@/components/common/ChartTooltip";
import { Delta } from "@/components/common/Delta";
import { useTheme } from "@/context/ThemeContext";
import { axisTick, chartTheme } from "@/lib/chartTheme";
import { fmtDate, fmtDayTime, fmtNum } from "@/lib/format";
import { cn } from "@/lib/utils";

const SERIES_COLORS = (t) => [t.primary, t.copper, t.teal, t.rose, t.cyan];

const parseT = (v) => {
  if (!v) return null;
  try {
    const d = typeof v === "string" ? parseISO(v) : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

const xLabel = (v, resolution, xType) => {
  if (xType !== "time") return String(v ?? "");
  const d = parseT(v);
  if (!d) return String(v ?? "");
  return resolution === "hour" ? format(d, "d. MMM HH:mm", { locale: nb }) : format(d, "d. MMM", { locale: nb });
};

const tickLabel = (v, resolution, xType, span) => {
  if (xType !== "time") return String(v ?? "");
  const d = parseT(v);
  if (!d) return String(v ?? "");
  if (resolution === "hour") return span > 2 ? format(d, "d. MMM", { locale: nb }) : format(d, "HH:mm");
  return span > 120 ? format(d, "MMM yy", { locale: nb }) : format(d, "d. MMM", { locale: nb });
};

const fmtVal = (v, unit) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "–";
  const abs = Math.abs(v);
  const s = abs >= 1000 ? fmtNum(v, 0) : abs >= 10 ? fmtNum(v, 1) : fmtNum(v, 2);
  return unit ? `${s} ${unit}` : s;
};

/* ----------------------------- chart ----------------------------- */
export const ArtifactChart = ({ chart, height = 280 }) => {
  const { isDark } = useTheme();
  const t = chartTheme(isDark);
  const colors = SERIES_COLORS(t);
  const { kind = "line", x_key: xKey = "t", x_type: xType = "category", series = [], data = [], resolution } = chart || {};
  const spanDays = useMemo(() => {
    if (xType !== "time" || data.length < 2) return 0;
    const a = parseT(data[0]?.[xKey]);
    const b = parseT(data[data.length - 1]?.[xKey]);
    return a && b ? (b - a) / 86400000 : 0;
  }, [data, xKey, xType]);
  const hasRight = series.some((s) => s.axis === "right");
  const unitOf = (key) => series.find((s) => s.key === key)?.unit || "";
  const common = { data, margin: { top: 8, right: hasRight ? 4 : 12, left: 0, bottom: 0 } };
  const grid = <CartesianGrid vertical={false} stroke={t.grid} strokeDasharray="3 6" />;
  const xAxis = <XAxis dataKey={xKey} tick={axisTick(t)} tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} tickFormatter={(v) => tickLabel(v, resolution, xType, spanDays)} interval="preserveStartEnd" />;
  const yLeft = <YAxis yAxisId="left" tick={axisTick(t)} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => fmtNum(v, Math.abs(v) < 10 ? 1 : 0)} />;
  const yRight = hasRight ? <YAxis yAxisId="right" orientation="right" tick={axisTick(t)} tickLine={false} axisLine={false} width={54} tickFormatter={(v) => fmtNum(v, Math.abs(v) < 10 ? 1 : 0)} /> : null;
  const tooltip = <Tooltip cursor={{ stroke: t.cursor, strokeOpacity: 0.2, strokeDasharray: "3 3", fill: t.cursor, fillOpacity: 0.04 }} content={<ChartTooltip formatter={(v, name, p) => fmtVal(v, unitOf(p?.dataKey))} labelFormatter={(l) => xLabel(l, resolution, xType)} />} />;

  if (kind === "hbar") {
    const s0 = series[0] || {};
    const h = Math.max(height, 36 * data.length + 40);
    return (
      <div style={{ height: h }} className="w-full">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 240 }}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }} barCategoryGap="28%">
            <CartesianGrid horizontal={false} stroke={t.grid} strokeDasharray="3 6" />
            <XAxis type="number" tick={axisTick(t)} tickLine={false} axisLine={false} tickFormatter={(v) => fmtNum(v, Math.abs(v) < 10 ? 1 : 0)} />
            <YAxis type="category" dataKey={xKey} tick={{ ...axisTick(t), fontSize: 12 }} tickLine={false} axisLine={false} width={120} />
            <Tooltip cursor={{ fill: t.cursor, fillOpacity: 0.04 }} content={<ChartTooltip formatter={(v) => fmtVal(v, s0.unit)} />} />
            <Bar dataKey={s0.key} name={s0.label || s0.key} radius={[0, 4, 4, 0]} maxBarSize={22} isAnimationActive={false}>
              {data.map((_, i) => (
                <Cell key={i} fill={t.primary} fillOpacity={0.9 - Math.min(0.5, i * 0.06)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div style={{ height }} className="w-full">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 240 }}>
        {kind === "bar" ? (
          <BarChart {...common} barCategoryGap="25%">
            {grid}
            {xAxis}
            {yLeft}
            {yRight}
            {tooltip}
            {series.map((s, i) => (s.axis === "right" && (kind === "bar") ? <Line key={s.key} yAxisId="right" type="monotone" dataKey={s.key} name={s.label} stroke={colors[i % colors.length]} strokeWidth={2} dot={false} isAnimationActive={false} /> : <Bar key={s.key} yAxisId="left" dataKey={s.key} name={s.label} fill={colors[i % colors.length]} fillOpacity={0.9} radius={[4, 4, 0, 0]} maxBarSize={40} isAnimationActive={false} />))}
          </BarChart>
        ) : kind === "area" ? (
          <AreaChart {...common}>
            {grid}
            {xAxis}
            {yLeft}
            {yRight}
            {tooltip}
            {series.map((s, i) => (
              <Area key={s.key} yAxisId={s.axis === "right" ? "right" : "left"} type="monotone" dataKey={s.key} name={s.label} stroke={colors[i % colors.length]} strokeWidth={2} fill={colors[i % colors.length]} fillOpacity={i === 0 ? 0.12 : 0.06} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: isDark ? "hsl(207 50% 10%)" : "#fff" }} connectNulls isAnimationActive={false} />
            ))}
          </AreaChart>
        ) : (
          <LineChart {...common}>
            {grid}
            {xAxis}
            {yLeft}
            {yRight}
            {tooltip}
            {series.map((s, i) => (
              <Line key={s.key} yAxisId={s.axis === "right" ? "right" : "left"} type="monotone" dataKey={s.key} name={s.label} stroke={colors[i % colors.length]} strokeWidth={2} dot={data.length <= 20} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const Legend = ({ series }) => {
  const { isDark } = useTheme();
  const colors = SERIES_COLORS(chartTheme(isDark));
  if (!series?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-muted-foreground">
      {series.map((s, i) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: colors[i % colors.length] }} /> {s.label}
          {s.unit ? <span className="text-muted-foreground/70">({s.unit})</span> : null}
        </span>
      ))}
    </div>
  );
};

/* ----------------------------- table ----------------------------- */
const STATUS_DOT = { "I drift": "status-dot-running", Redusert: "status-dot-reduced", Stanset: "status-dot-stopped", Vedlikehold: "status-dot-maintenance", Åpen: "bg-rose-500", Kvittert: "bg-amber-500", Lukket: "bg-slate-400" };
const SEV_CLASS = { Kritisk: "text-rose-700 dark:text-rose-300", Advarsel: "text-amber-700 dark:text-amber-300", Info: "text-muted-foreground" };

const cellValue = (col, v) => {
  if (v === null || v === undefined || v === "") return <span className="text-muted-foreground/60">–</span>;
  switch (col.format) {
    case "number":
      return <span className="metric">{typeof v === "number" ? fmtNum(v, col.decimals ?? (Math.abs(v) < 10 ? 2 : Math.abs(v) < 1000 ? 1 : 0)) : v}</span>;
    case "pct":
      return <span className="metric">{typeof v === "number" ? `${fmtNum(v, col.decimals ?? 1)} %` : v}</span>;
    case "bar": {
      const pct = Math.max(0, Math.min(100, Number(v) || 0));
      return (
        <span className="flex items-center justify-end gap-2">
          <span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
            <span className="block h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </span>
          <span className="metric w-10 text-right">{fmtNum(pct, 0)} %</span>
        </span>
      );
    }
    case "date":
      return <span className="metric whitespace-nowrap">{fmtDate(v)}</span>;
    case "datetime":
      return <span className="metric whitespace-nowrap">{fmtDayTime(v)}</span>;
    case "status":
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className={cn("status-dot", STATUS_DOT[v] || "bg-slate-400")} /> {v}
        </span>
      );
    case "severity":
      return <span className={cn("font-medium", SEV_CLASS[v] || "")}>{v}</span>;
    default:
      return typeof v === "number" ? <span className="metric">{fmtNum(v, Math.abs(v) < 10 ? 2 : 0)}</span> : typeof v === "boolean" ? (v ? "Ja" : "Nei") : String(v);
  }
};

const numeric = (col) => ["number", "pct", "bar"].includes(col.format);

export const ArtifactTable = ({ columns = [], rows = [], maxRows = 60 }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? rows : rows.slice(0, 12);
  return (
    <div className="overflow-x-auto">
      <Table className="text-[14px]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {columns.map((c) => (
              <TableHead key={c.key} className={cn("h-10 whitespace-nowrap text-[13px] font-medium", numeric(c) && "text-right")}>
                {c.label}
                {c.unit ? <span className="ml-1 text-muted-foreground/70">({c.unit})</span> : null}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((r, i) => (
            <TableRow key={i} className="odd:bg-muted/30">
              {columns.map((c) => (
                <TableCell key={c.key} className={cn("py-2.5", numeric(c) && "text-right", c.format === "text" || !c.format ? "max-w-[320px] truncate" : "")}>
                  {cellValue(c, r[c.key])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 12 ? (
        <button type="button" onClick={() => setExpanded((e) => !e)} className="w-full border-t border-border py-2 text-[13px] font-medium text-primary hover:bg-muted/50" data-testid="ai-artifact-table-toggle">
          {expanded ? "Vis færre" : `Vis alle ${Math.min(rows.length, maxRows)} rader`}
        </button>
      ) : null}
    </div>
  );
};

/* ----------------------------- kpis ------------------------------ */
export const ArtifactKpis = ({ items = [] }) => (
  <div className={cn("grid gap-px bg-border", items.length <= 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-3")}>
    {items.map((it, i) => (
      <div key={i} className="bg-card px-4 py-3.5" data-testid="ai-kpi-item">
        <div className="text-[13px] text-muted-foreground">{it.label}</div>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5">
          <span className={cn("readout-value text-[24px]", it.tone === "alert" ? "text-rose-600 dark:text-rose-400" : "text-foreground")}>{typeof it.value === "number" ? fmtNum(it.value, Math.abs(it.value) < 10 && it.value % 1 !== 0 ? 1 : 0) : it.value ?? "–"}</span>
          {it.unit ? <span className="text-[13px] text-muted-foreground">{it.unit}</span> : null}
          {it.delta_pct !== null && it.delta_pct !== undefined ? <Delta value={it.delta_pct} className="ml-1" /> : null}
        </div>
        {it.sub ? <div className="mt-1 text-[12.5px] text-muted-foreground">{it.sub}</div> : null}
      </div>
    ))}
  </div>
);

/* --------------------------- container --------------------------- */
const toCsv = (artifact) => {
  let cols = [];
  let rows = [];
  if (artifact.type === "table") {
    cols = artifact.columns.map((c) => c.key);
    rows = artifact.rows;
  } else if (artifact.type === "chart") {
    cols = [artifact.chart.x_key, ...artifact.chart.series.map((s) => s.key)];
    rows = artifact.chart.data;
  } else if (artifact.type === "kpis") {
    cols = ["label", "value", "unit", "delta_pct", "sub"];
    rows = artifact.items;
  }
  const esc = (v) => (v === null || v === undefined ? "" : `"${String(v).replace(/"/g, '""')}"`);
  return [cols.join(";"), ...rows.map((r) => cols.map((c) => esc(r[c])).join(";"))].join("\n");
};

const download = (artifact) => {
  const blob = new Blob(["\ufeff" + toCsv(artifact)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(artifact.title || "hydrohub").replace(/[^\w\dæøåÆØÅ -]/g, "").trim().replace(/\s+/g, "-").toLowerCase()}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Artifact container: title, subtitle, source + chart/table/kpis body. */
const ArtifactCardInner = ({ artifact, pinned = false, onPin, compact = false, className }) => {
  if (!artifact) return null;
  return (
    <figure className={cn("min-w-0 overflow-hidden rounded-lg border border-border bg-card", className)} data-testid={`ai-artifact-${artifact.type}`}>
      <figcaption className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-[14.5px] font-semibold">{artifact.title}</div>
          {artifact.subtitle ? <div className="mt-0.5 truncate text-[12.5px] text-muted-foreground">{artifact.subtitle}</div> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <UiTooltip>
            <TooltipTrigger asChild>
              <Button variant="quiet" size="icon-xs" onClick={() => download(artifact)} aria-label="Last ned som CSV" data-testid="ai-artifact-download-button">
                <Download className="!h-3.5 !w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Last ned CSV</TooltipContent>
          </UiTooltip>
          {onPin ? (
            <UiTooltip>
              <TooltipTrigger asChild>
                <Button variant="quiet" size="icon-xs" onClick={() => onPin(artifact)} aria-label={pinned ? "Løsne" : "Fest i panelet"} data-testid="ai-artifact-pin-button">
                  {pinned ? <PinOff className="!h-3.5 !w-3.5" /> : <Pin className="!h-3.5 !w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{pinned ? "Løsne fra panelet" : "Fest i panelet"}</TooltipContent>
            </UiTooltip>
          ) : null}
        </div>
      </figcaption>
      <div className={cn(artifact.type === "chart" ? "p-3 pt-4" : "")}>
        {artifact.type === "chart" ? (
          <>
            <ArtifactChart chart={artifact.chart} height={compact ? 220 : 280} />
            <div className="mt-2 px-1">
              <Legend series={artifact.chart?.series} />
            </div>
          </>
        ) : artifact.type === "table" ? (
          <ArtifactTable columns={artifact.columns} rows={artifact.rows} />
        ) : artifact.type === "kpis" ? (
          <ArtifactKpis items={artifact.items} />
        ) : null}
      </div>
      {artifact.source ? <div className="border-t border-border px-4 py-2 text-[12px] text-muted-foreground">Kilde: {artifact.source}</div> : null}
    </figure>
  );
};

export const ArtifactCard = memo(ArtifactCardInner, (a, b) => a.artifact === b.artifact && a.pinned === b.pinned && a.compact === b.compact && a.onPin === b.onPin && a.className === b.className);
